// Budgets + slice sizing for the navigational identifier-index build.
//
// Why this exists: the pre-deferral builder issued ONE un-paged query per
// object-body catalog (`sys.sql_modules` and friends) and parsed every body
// synchronously. On a 7,000-relation / 3,000-procedure SQL Server database
// that was 34 MB of definitions arriving as a single inline IPC result set,
// followed by a multi-second main-thread parse. Three bounds fix it:
//
//   1. `NAV_INDEX_PAGE_ROWS` — bodies are large, so the nav feed pages far
//      smaller than the schema snapshot's 5,000-row pages.
//   2. `NAV_INDEX_BODY_BYTE_BUDGET` — a TOTAL memory bound on the body text
//      one build will hold, spent across every artefact of every feed. Row
//      counts are the wrong unit here: 200 views can outweigh 20,000 CHECK
//      clauses.
//   3. `NAV_INDEX_SLICE_BUDGET_MS` — how long one parse slice may own the
//      main thread before the builder yields.
//
// The budget is a mutable tracker rather than a number because it is SHARED:
// the parseable feed and the procedural feed of one build spend from the same
// allowance, and the fetchers stop paging as soon as it is gone.

/**
 * Rows per page of a nav body query. Deliberately an order of magnitude below
 * the schema snapshot's `INTROSPECTION_PAGE_ROWS` (5,000): every row of these
 * queries carries a whole view / routine / constraint DEFINITION, so 500 rows
 * is already a multi-megabyte page on a large catalog. Stays well under the
 * query cache's windowing threshold, so a page always arrives inline.
 */
export const NAV_INDEX_PAGE_ROWS = 500;

/**
 * TOTAL body text one index build will hold, across every artefact of both
 * feeds. Counted in UTF-16 code units of the body columns (`.length`), which
 * is the same number as bytes for the ASCII-dominant DDL these catalogs
 * return and within 2× for anything else — close enough for a memory bound,
 * and free to compute.
 *
 * When it is spent the fetchers stop paging, the index is marked `partial`,
 * and the clip is reported through `reportIntrospectionTruncation` under the
 * `'routines'` artefact so the IntelliSense pill can name it.
 */
export const NAV_INDEX_BODY_BYTE_BUDGET = 64 * 1024 * 1024;

/**
 * Hard row ceiling per nav artefact, the row-count companion to the byte
 * budget. Oracle's `ALL_SOURCE` feed is one row per SOURCE LINE, so a catalog
 * can produce far more rows than objects; this stops a pathological one from
 * paging forever even when each line is tiny. Tripping it marks the index
 * `partial` exactly like the byte budget does.
 */
export const NAV_INDEX_ROW_CEILING = 500_000;

/**
 * How long one parse slice may own the main thread before
 * `buildIndexForConnection` yields. 8 ms leaves the 16.7 ms frame budget
 * mostly intact and is far below the 50 ms long-task threshold the perf gate
 * (`snapshotLoadLongTasks.perf.spec.ts`) asserts on.
 */
export const NAV_INDEX_SLICE_BUDGET_MS = 8;

/**
 * Procedural bodies go through the ANTLR procedural grammars, which are
 * enormously more expensive per body than the SELECT-body pipeline — one
 * parse can exceed the whole slice budget on its own. So the procedural feed
 * yields after EVERY body rather than time-slicing a batch of them.
 */
export const NAV_INDEX_PROCEDURAL_PER_SLICE = 1;

/** Shared, mutable body-byte allowance for one index build. */
export type NavBodyBudget = {
  /** Charge `bytes` against the allowance. */
  readonly spend: (bytes: number) => void;
  /**
   * Force the allowance into the exhausted state without charging bytes —
   * used when a row ceiling trips, which is the same user-visible outcome
   * ("we stopped early, the index is incomplete").
   */
  readonly exhaust: () => void;
  readonly isExhausted: () => boolean;
  readonly bytesSpent: () => number;
};

export function createNavBodyBudget(limitBytes: number = NAV_INDEX_BODY_BYTE_BUDGET): NavBodyBudget {
  let spent = 0;
  let forced = false;
  return {
    spend: (bytes: number) => {
      spent += Math.max(0, bytes);
    },
    exhaust: () => {
      forced = true;
    },
    isExhausted: () => forced || spent >= limitBytes,
    bytesSpent: () => spent,
  };
}

/**
 * Body text carried by one catalog row, in the unit the budget counts.
 * `bodyColumns` names the columns whose text is the body (`body`, `text`, …)
 * so the accounting ignores the small identifier columns travelling with it.
 */
export function navBodyBytesOf(
  row: Readonly<Record<string, unknown>>,
  bodyColumns: readonly string[],
): number {
  let total = 0;
  for (const column of bodyColumns) {
    const value = row[column];
    if (typeof value === 'string') total += value.length;
    else if (value !== null && value !== undefined) total += String(value).length;
  }
  return total;
}
